import { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isNil, noop, values } from 'lodash';
// Import Components
import CJFilterCondition from 'components/CustomerJourney/forms/CJFilterCondition';
import CustomerJourneyVisualizationTemplateGroup from './CustomerJourneyVisualizationTemplateGroup';
import CustomerJourneyVisualizationReward from './CustomerJourneyVisualizationReward';
import CustomerJourneyVisualizationButton from './CustomerJourneyVisualizationButton';
import CustomerJourneyVisualizationUnlock from './CustomerJourneyVisualizationUnlock';
import CustomerJourneyVisualizationDueDate from './CustomerJourneyVisualizationDueDate';
import CustomerJourneyVisualizationProgress from './CustomerJourneyVisualizationProgress';
import CustomerJourneyVisualizationIconPicker from './CustomerJourneyVisualizationIconPicker';
// Import UI Components
import { ExtendedInput, Checkbox } from '@geneui/components';
// Import Services
import { Helpers } from 'services';
import { isCJVisualizationIconAllowed } from 'services/customerJourney/helpers';
// Import Constants
import { l, LogicTypesValues } from 'constants/common';

const { itemsIsValid } = Helpers;

const CustomerJourneyVisualizationSubStep = ({
    collectedProperties,
    node,
    elements,
    option,
    rewardOption,
    rewardClientBonusIdOption,
    unlockOption,
    dueDateOption,
    progressOption,
    availableSteps,
    icons,
    getUpdate,
    defaultValue,
    stepNameGenerator,
}) => {
    const [filterConditionFilters, setFilterConditionFilters] = useState(defaultValue?.condition);
    const conditionUpdateHandler = (filterValue) => {
        setFilterConditionFilters((prev) => ({ ...prev, ...filterValue }));
    };
    const [templateData, setTemplateData] = useState(defaultValue?.templates);
    const [reward, setReward] = useState(defaultValue?.reward);
    const rewardUpdateHandler = (rewardValue) => {
        setReward((prev) => ({ ...prev, ...rewardValue }));
    };
    const [button, setButton] = useState(defaultValue?.button);
    const buttonUpdateHandler = (buttonValue) => {
        setButton((prev) => ({ ...prev, ...buttonValue }));
    };
    const [unlock, setUnlock] = useState(defaultValue?.unlock);
    const unlockUpdateHandler = (unlockValue) => {
        setUnlock((prev) => ({ ...prev, ...unlockValue }));
    };
    const [dueDate, setDueDate] = useState(defaultValue?.dueDate);
    const dueDateUpdateHandler = (dueDateValue) => {
        setDueDate((prev) => ({ ...prev, ...dueDateValue }));
    };
    const [progress, setProgress] = useState(defaultValue?.progress);
    const progressUpdateHandler = (progressValue) => {
        setProgress((prev) => ({ ...prev, ...progressValue }));
    };

    // get update only blur
    const [name, setName] = useState(defaultValue?.name);
    const [description, setDescription] = useState(defaultValue?.description);
    const [isHidden, setIsHidden] = useState(defaultValue?.isHidden);
    const [icon, setIcon] = useState(defaultValue?.icon);

    const init = noop;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    const cleanUp = () => {
        return noop;
    };

    useEffect(() => {
        getUpdate({
            name: name,
            description: description,
            isHidden: isHidden,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, description, isHidden]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    const { t } = useTranslation();

    const nameChangeHandler = (e) => {
        setName(e.target.value);
    };

    const descriptionChangeHandler = (e) => {
        setDescription(e.target.value);
    };

    const nameBlurHandler = () => {
        const tmpName = stepNameGenerator(name);
        setName(tmpName);
    };

    const isHiddenChangeHandler = (event) => {
        setIsHidden(event.target.checked);
    };

    const descriptionBlurHandler = () => {
        setDescription(description);
    };
    useEffect(() => {
        const isRewardValid = isNil(reward) || reward?.isValid === true;
        const isButtonValid = isNil(button) || button?.isValid === true;
        const isUnlockValid = isNil(unlock) || unlock?.isValid === true;
        const isDueDateValid = isNil(dueDate) || dueDate?.isValid === true;
        const isProgressValid = isNil(progress) || progress?.isValid === true;
        const isIconValid = isCJVisualizationIconAllowed(icons, icon);
        const isSubStepValid =
            itemsIsValid(values(templateData)) &&
            isIconValid &&
            !!filterConditionFilters?.isValid &&
            isRewardValid &&
            isButtonValid &&
            isUnlockValid &&
            isDueDateValid &&
            isProgressValid;
        getUpdate({
            isValid: isSubStepValid,
            icon: icon,
            templates: templateData,
            condition: filterConditionFilters,
            reward: reward,
            button: button,
            unlock: unlock,
            dueDate: dueDate,
            progress: progress,
        });
        // DEPS: [getUpdate]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateData, filterConditionFilters, icon, icons, reward, button, unlock, dueDate, progress]);

    const subStepTemplateChangeHandler = (languageKey, data) => {
        setTemplateData((prev) => {
            return { ...prev, [languageKey]: { ...prev[languageKey], ...data } };
        });
    };

    return (
        <div className="crm-cj-visualization-step">
            <div className="crm-cj-visualization-step-block">
                <div className="c-input-group-wrapper">
                    <ExtendedInput
                        appearance="outline"
                        inputSize="default"
                        labelAppearance="swap"
                        placeholder={t(l.Name)}
                        onChange={nameChangeHandler}
                        onBlur={nameBlurHandler}
                        value={isNil(name) ? '' : name}
                    />
                    <ExtendedInput
                        appearance="outline"
                        inputSize="default"
                        labelAppearance="swap"
                        placeholder={t(l.Description)}
                        onChange={descriptionChangeHandler}
                        onBlur={descriptionBlurHandler}
                        value={isNil(description) ? '' : description}
                    />
                </div>
                <CustomerJourneyVisualizationIconPicker
                    icons={icons}
                    value={icon}
                    onChange={setIcon}
                    isRequired={false}
                />
                <Checkbox label={t(l.IsHidden)} checked={isHidden} onChange={isHiddenChangeHandler} />
            </div>
            <CustomerJourneyVisualizationTemplateGroup
                templates={templateData}
                onChange={subStepTemplateChangeHandler}
            />
            <CJFilterCondition
                hasSectionLayout={true}
                collectedProperties={collectedProperties}
                node={node}
                elements={elements}
                option={option}
                getUpdate={conditionUpdateHandler}
                defaultValue={filterConditionFilters}
                groupLabel={t(l.Filters)}
            />
            {!isNil(reward) && (
                <CustomerJourneyVisualizationReward
                    collectedProperties={collectedProperties}
                    node={node}
                    elements={elements}
                    option={rewardOption}
                    rewardClientBonusIdOption={rewardClientBonusIdOption}
                    getUpdate={rewardUpdateHandler}
                    defaultValue={reward}
                />
            )}
            {!isNil(button) && (
                <CustomerJourneyVisualizationButton getUpdate={buttonUpdateHandler} defaultValue={button} />
            )}
            {!isNil(unlock) && (
                <CustomerJourneyVisualizationUnlock
                    collectedProperties={collectedProperties}
                    node={node}
                    elements={elements}
                    option={unlockOption}
                    availableSteps={availableSteps}
                    getUpdate={unlockUpdateHandler}
                    defaultValue={unlock}
                />
            )}
            {!isNil(dueDate) && (
                <CustomerJourneyVisualizationDueDate
                    collectedProperties={collectedProperties}
                    node={node}
                    elements={elements}
                    option={dueDateOption}
                    getUpdate={dueDateUpdateHandler}
                    defaultValue={dueDate}
                />
            )}
            {!isNil(progress) && (
                <CustomerJourneyVisualizationProgress
                    collectedProperties={collectedProperties}
                    node={node}
                    elements={elements}
                    option={progressOption}
                    getUpdate={progressUpdateHandler}
                    defaultValue={progress}
                />
            )}
        </div>
    );
};

CustomerJourneyVisualizationSubStep.propTypes = {
    defaultValue: PropTypes.shape({
        templates: PropTypes.object,
        name: PropTypes.string,
        description: PropTypes.string,
        isHidden: PropTypes.bool,
        icon: PropTypes.string,
        condition: PropTypes.shape({
            filters: PropTypes.array,
            logic: PropTypes.string,
        }),
        reward: PropTypes.shape({
            showReward: PropTypes.bool,
            templates: PropTypes.object,
            condition: PropTypes.shape({
                filters: PropTypes.array,
                logic: PropTypes.string,
            }),
            clientBonusId: PropTypes.object,
            isValid: PropTypes.bool,
        }),
        button: PropTypes.shape({
            showButton: PropTypes.bool,
            templates: PropTypes.object,
            route: PropTypes.shape({
                page: PropTypes.string,
                extraInfo: PropTypes.object,
                isValid: PropTypes.bool,
            }),
            isValid: PropTypes.bool,
        }),
        unlock: PropTypes.shape({
            isLocked: PropTypes.bool,
            type: PropTypes.number,
            stepUid: PropTypes.string,
            condition: PropTypes.shape({
                filters: PropTypes.array,
                logic: PropTypes.string,
            }),
            dateTime: PropTypes.string,
            isValid: PropTypes.bool,
        }),
        dueDate: PropTypes.shape({
            isEnabled: PropTypes.bool,
            type: PropTypes.number,
            value: PropTypes.object,
            isValid: PropTypes.bool,
        }),
        progress: PropTypes.shape({
            showProgress: PropTypes.bool,
            templates: PropTypes.object,
            type: PropTypes.string,
            current: PropTypes.object,
            goal: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
            isValid: PropTypes.bool,
        }),
    }),
    icons: PropTypes.array,
    node: PropTypes.object,
    elements: PropTypes.array,
    option: PropTypes.object,
    rewardOption: PropTypes.object,
    rewardClientBonusIdOption: PropTypes.object,
    unlockOption: PropTypes.object,
    dueDateOption: PropTypes.object,
    progressOption: PropTypes.object,
    availableSteps: PropTypes.array,
    stepNameGenerator: PropTypes.func.isRequired,
    collectedProperties: PropTypes.array,
    getUpdate: PropTypes.func,
    addTemplateValidationProperties: PropTypes.func,
    removeTemplateValidationProperties: PropTypes.func,
};

CustomerJourneyVisualizationSubStep.defaultProps = {
    defaultValue: {
        templates: {},
        condition: {
            logic: LogicTypesValues.and,
            filters: [],
        },
        isHidden: false,
        name: '',
        description: '',
        icon: null,
        reward: null,
        button: null,
        unlock: null,
        dueDate: null,
        progress: null,
    },
    icons: [],
    collectedProperties: [],
    node: null,
    elements: null,
    option: null,
    rewardOption: null,
    rewardClientBonusIdOption: null,
    unlockOption: null,
    dueDateOption: null,
    progressOption: null,
    availableSteps: [],
    getUpdate: noop,
    addTemplateValidationProperties: noop,
    removeTemplateValidationProperties: noop,
};

export default memo(CustomerJourneyVisualizationSubStep);
